import { useCallback, useEffect, useRef, useState } from 'react';

import { isHeadlessPrintRoute } from '../utils/printRoute';

// How often to ask the browser to re-check /sw.js while the app stays open. The
// browser only checks for a new service worker on navigation, so a long-lived
// surface that never navigates (an all-day-open PWA window) would otherwise never
// surface the update prompt. One hour balances freshness against needless network
// chatter; a visibilitychange check catches reopened PWAs sooner.
const UPDATE_POLL_INTERVAL_MS = 60 * 60 * 1000;

let serviceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration> | null =
  null;
let controllerChangeWired = false;
let updatePollingStarted = false;
// Per-tab: did THIS tab's user accept the update? Gates the controllerchange
// reload so one tab accepting can't reload another tab mid-form (see below).
let userAcceptedUpdate = false;

const isCypressRuntime = () =>
  typeof window !== 'undefined' &&
  Boolean((window as Window & { Cypress?: unknown }).Cypress);

const registerServiceWorkerOnce = () => {
  if (serviceWorkerRegistrationPromise) {
    return serviceWorkerRegistrationPromise;
  }

  serviceWorkerRegistrationPromise = navigator.serviceWorker
    // updateViaCache:'none' -> the browser never serves /sw.js itself from HTTP
    // cache when checking for updates. Caddy already sends no-store for /sw.js, so
    // this is belt-and-suspenders: update detection keeps working even if that
    // header ever regresses.
    .register('/sw.js', { updateViaCache: 'none' })
    .then((registration) => {
      console.log('SW registered:', registration);
      return registration;
    })
    .catch((error) => {
      serviceWorkerRegistrationPromise = null;
      console.log('SW registration failed:', error);
      throw error;
    });

  return serviceWorkerRegistrationPromise;
};

// Reload the page when a newly-approved worker takes control -- but only in the tab
// whose own user accepted the update. In the prompt flow, the user's click posts
// SKIP_WAITING, the worker activates, clientsClaim claims the page, and the
// resulting controllerchange lands here to swap in the new bundle (the running JS is
// still the OLD one at that point).
//
// The `userAcceptedUpdate` gate matters for multi-tab: clientsClaim fires
// controllerchange in EVERY open tab the moment any one tab activates the worker.
// Without the gate, a user typing in another tab would be reloaded without ever
// clicking. Tabs that did not accept keep their prompt and reload on their own click
// (handled in updateServiceWorker).
const wireControllerChangeReload = () => {
  if (controllerChangeWired) {
    return;
  }
  // Guarded for non-browser/test environments where the SW container is a stub.
  if (typeof navigator.serviceWorker?.addEventListener !== 'function') {
    return;
  }
  controllerChangeWired = true;

  let reloading = false;
  // If the page is NOT yet controlled (first visit), the first controllerchange is
  // merely the initial clientsClaim adopting the page; take it silently.
  let controlled = Boolean(navigator.serviceWorker.controller);

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!controlled) {
      controlled = true;
      return;
    }
    if (!userAcceptedUpdate) {
      return;
    }
    if (reloading) {
      return;
    }
    reloading = true;
    window.location.reload();
  });
};

// Periodically nudge the browser to look for a new /sw.js so long-lived, never-
// navigating surfaces still discover deploys (and show the prompt). Runs once for
// the app's lifetime, matching the register-once singleton above.
const startUpdatePolling = () => {
  if (updatePollingStarted) {
    return;
  }
  updatePollingStarted = true;

  const checkForUpdate = () => {
    registerServiceWorkerOnce()
      .then((registration) => registration.update())
      .catch(() => {
        // Transient/offline failures are fine; the next tick retries.
      });
  };

  setInterval(checkForUpdate, UPDATE_POLL_INTERVAL_MS);

  if (typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    });
  }
};

export interface ServiceWorkerUpdateState {
  // A new version has installed and is waiting -- show the reload prompt.
  needRefresh: boolean;
  // Apply the waiting update: activate the new worker, which reloads the page.
  updateServiceWorker: () => void;
}

export const useServiceWorker = (): ServiceWorkerUpdateState => {
  const [needRefresh, setNeedRefresh] = useState(false);
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);

  useEffect(() => {
    // Skip SW registration on the /print/ route: it is the standalone PDF surface,
    // loaded ONLY by the backend's headless-chrome PDF worker (real users never
    // navigate to it). That headless browser shares one long-lived, persistent
    // browser context across every render (chromedp NewRemoteAllocator + NewContext,
    // no incognito; the container is restart: unless-stopped). If the print route
    // ever registered a service worker there, workbox's navigateFallback could serve
    // a STALE cached app shell on a later render — and if that shell's /api calls
    // fail (e.g. mid-deploy, before the internal `http://caddy` origin is live),
    // PrintMoldOrderReport flips __PRINT_READY on its error state and the worker
    // captures a silently-WRONG PDF (HTTP 200), with no Go-renderer fallback. The
    // headless context only ever loads /print/ URLs, so skipping registration here
    // keeps it permanently SW-free and makes every render hit the network for the
    // current code + live data. (Pre-cutover the headless worker rendered a local
    // file:// HTML file and never touched the app origin, so no app-origin SW exists
    // in that context today to clear.)
    if (
      isCypressRuntime() ||
      isHeadlessPrintRoute() ||
      !('serviceWorker' in navigator)
    ) {
      return;
    }

    let cancelled = false;
    const listenerCleanups: Array<() => void> = [];

    const promptForWorker = (worker: ServiceWorker | null | undefined) => {
      if (!worker || cancelled) {
        return;
      }
      waitingWorkerRef.current = worker;
      setNeedRefresh(true);
    };

    const watchInstallingWorker = (worker: ServiceWorker | null) => {
      if (!worker) {
        return;
      }
      const onStateChange = () => {
        // 'installed' while a controller already exists => a NEW version finished
        // installing behind the current one (it is now "waiting"). The first-ever
        // install has no controller and is intentionally NOT prompted -- there is
        // nothing to update from.
        if (
          worker.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          promptForWorker(worker);
        }
      };
      worker.addEventListener('statechange', onStateChange);
      listenerCleanups.push(() => {
        if (typeof worker.removeEventListener === 'function') {
          worker.removeEventListener('statechange', onStateChange);
        }
      });
    };

    const trackRegistration = (registration: ServiceWorkerRegistration) => {
      if (typeof registration.addEventListener !== 'function') {
        return;
      }
      // An update may already be parked from a previous load or another tab.
      if (registration.waiting && navigator.serviceWorker.controller) {
        promptForWorker(registration.waiting);
      }
      // Or a worker may already be mid-install before this listener attaches
      // (a deploy landing exactly during page load) -- catch its statechange too.
      if (registration.installing) {
        watchInstallingWorker(registration.installing);
      }
      const onUpdateFound = () =>
        watchInstallingWorker(registration.installing);
      registration.addEventListener('updatefound', onUpdateFound);
      listenerCleanups.push(() => {
        if (typeof registration.removeEventListener === 'function') {
          registration.removeEventListener('updatefound', onUpdateFound);
        }
      });
    };

    const start = () => {
      wireControllerChangeReload();
      registerServiceWorkerOnce()
        .then((registration) => {
          if (cancelled) {
            return;
          }
          trackRegistration(registration);
          startUpdatePolling();
        })
        .catch(() => {
          // Error is already logged in registerServiceWorkerOnce; keep this non-throwing.
        });
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', start);
      listenerCleanups.forEach((remove) => remove());
    };
  }, []);

  const updateServiceWorker = useCallback(() => {
    userAcceptedUpdate = true;
    const worker = waitingWorkerRef.current;
    if (worker && worker.state === 'installed') {
      // Waiting worker in THIS tab: activate it. The message handler vite-plugin-pwa
      // injects into sw.js calls skipWaiting(); clientsClaim then fires
      // controllerchange -> wireControllerChangeReload reloads this tab.
      worker.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // The worker was already activated by another tab (we ignored its
      // controllerchange because this tab had not accepted yet). A plain reload now
      // pulls the new shell + assets, which the already-active worker serves.
      window.location.reload();
    }
  }, []);

  return { needRefresh, updateServiceWorker };
};
